import React, { createContext, useContext, useState, useMemo, useLayoutEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { getDesignTokens } from './theme';

const ColorModeContext = createContext({ toggleColorMode: () => {}, mode: 'light' });

const getInitialColorMode = () => {
  const saved = localStorage.getItem('colorMode');
  if (saved === 'dark' || saved === 'light') return saved;
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

export const ColorModeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => getInitialColorMode());

  useLayoutEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    // Disable CSS transitions for the duration of the switch. Without this the
    // global `transition: all 0.25s` rules fade hundreds of elements at once,
    // which is what makes dark-mode switching feel slow. The class is removed
    // after the browser has painted the new theme (two rAFs), so normal hover
    // transitions still work afterwards.
    root.classList.add('theme-switching');

    // Set for CSS [data-theme="dark"] selector
    root.setAttribute('data-theme', mode);
    root.style.colorScheme = mode;

    // Set for.dark /.light class selectors (your SalesOrder.css uses this)
    root.classList.remove('light', 'dark');
    root.classList.add(mode);
    body.classList.remove('light', 'dark');
    body.classList.add(mode);
    body.setAttribute('data-theme', mode);

    localStorage.setItem('colorMode', mode);

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => root.classList.remove('theme-switching'));
    });

    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      root.classList.remove('theme-switching');
    };
  }, [mode]);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prev) => (prev === 'light'? 'dark' : 'light'));
      },
      mode,
      isDark: mode === 'dark',
    }),
    [mode]
  );

  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export const useColorMode = () => useContext(ColorModeContext);